
import React from "react";
import { 
  Share2, 
  Link2, 
  Facebook, 
  Twitter, 
  Linkedin,
  Copy
} from "lucide-react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { buildShareText, sharePost } from "@/utils/share";
import { SocialMetaTags } from "@/components/SocialMetaTags";

interface ShareMenuProps {
  title: string;
  url: string;
  imageUrl: string;
}

export function ShareMenu({ title, url, imageUrl }: ShareMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Texto chamativo de exemplo para compartilhar
  const previewText = title 
    ? `${title} Confira a arte completa! ✨`
    : "Veja esta arte incrível! ✨";
  const hashtag = "ArteDigital";

  // O texto do post será sempre com link + hashtag
  const shareText = buildShareText({
    text: previewText,
    url,
    hashtag
  });

  // Ensure image URL is absolute
  const absoluteImageUrl = imageUrl?.startsWith('http') 
    ? imageUrl 
    : `${window.location.origin}${imageUrl?.startsWith('/') ? '' : '/'}${imageUrl || ''}`;

  const shareOptions = [
    {
      name: "Twitter",
      icon: <Twitter className="w-4 h-4" />,
      onClick: () => {
        // Inclui texto chamativo, link e hashtag
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
        window.open(twitterUrl, '_blank');
        toast("Compartilhado no Twitter");
      },
      color: "bg-sky-500 hover:bg-sky-600"
    },
    {
      name: "Facebook",
      icon: <Facebook className="w-4 h-4" />,
      onClick: () => {
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`;
        window.open(fbUrl, '_blank');
        toast("Compartilhado no Facebook");
      },
      color: "bg-blue-600 hover:bg-blue-700"
    },
    {
      name: "Copiar Link",
      icon: <Copy className="w-4 h-4" />,
      onClick: () => {
        navigator.clipboard.writeText(url)
          .then(() => {
            toast("Link copiado para a área de transferência");
          })
          .catch((error) => {
            console.error("Erro ao copiar link:", error);
            toast("Erro ao copiar link");
          });
      },
      color: "bg-gray-600 hover:bg-gray-700"
    },
    {
      name: "Compartilhar",
      icon: <Share2 className="w-4 h-4" />,
      onClick: async () => {
        try {
          const result = await sharePost({
            text: previewText,
            imageUrl: absoluteImageUrl,
            url,
            hashtag,
          });
          if (!result) {
            toast("Compartilhamento nativo não suportado neste dispositivo");
          }
        } catch (error) {
          console.error("Erro ao compartilhar:", error);
          toast("Erro ao compartilhar conteúdo");
        }
      },
      color: "bg-neutral-800 hover:bg-neutral-900"
    },
  ];

  return (
    <>
      {isOpen && (
        <SocialMetaTags
          title={title || "InkWebtoons"}
          description={previewText}
          imageUrl={absoluteImageUrl}
          url={url}
        />
      )}
      
      <Button 
        variant="outline" 
        className="flex items-center gap-2 px-4 py-2 rounded-full" 
        onClick={() => setIsOpen(true)}
      >
        <Share2 className="h-4 w-4" />
        Compartilhar
      </Button>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <DialogTitle>Compartilhar</DialogTitle>
            <DialogDescription>
              Compartilhe este conteúdo nas redes sociais
              <div className="px-0 py-2 text-xs text-gray-400">
                <span>Prévia:</span>
                <div className="mt-1 p-2 bg-neutral-100 dark:bg-neutral-800 rounded break-words">
                  {shareText}
                </div>
              </div>
            </DialogDescription>
            <div className="grid grid-cols-2 gap-3 py-2">
              {shareOptions.map((option) => (
                <motion.div 
                  key={option.name}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center"
                >
                  <button
                    onClick={option.onClick}
                    className={`${option.color} text-white p-3 rounded-full transition-colors`}
                    aria-label={`Compartilhar no ${option.name}`}
                  >
                    {option.icon}
                  </button>
                  <span className="text-xs mt-1">{option.name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </DialogContent>
      </Dialog>
    </>
  );
}
